import React, { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useUser } from '../../components/UserContext';
import { loginRequest } from "../../authConfig";
import { DimensionUnitType, DimensionDisplayType } from "../../models/UserProfile";
import {
    Box, IconButton, Typography, Menu, MenuItem,
    FormControl, Select, InputLabel, Drawer, List, ListItem, ListItemText, Divider,
    Tooltip, Switch, FormGroup, FormControlLabel, ToggleButtonGroup, ToggleButton, FormLabel
} from '@mui/material';

import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutButton from '../LogoutButton';
import MenuIcon from '@mui/icons-material/Menu';
import PaletteIcon from '@mui/icons-material/Palette';
import CollectionsIcon from '@mui/icons-material/Collections';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import { SelectChangeEvent } from '@mui/material/Select';
import { useCurrency } from '../CurrencyContext';
import ClientSelector from '../ClientSelector';
import { useNavigate, useLocation } from 'react-router';
import { DrawerSearch } from '../../components/DrawerSearch';
import { SearchData } from '../../models/SearchElements';
import CasesIcon from '@mui/icons-material/Cases';
import { useFilter } from '../../components/Filter/ArtworkFilterContext';
import { PieChart } from '@mui/icons-material';
import logo from "../../assets/logo_initials_circle.png";  // "../../assets/White_trans.png";

//License Check
import type { LicenseCheck } from "../../models/License";
import { useLicenseChecker } from '../../hooks/useLicenseCheck';
import LicenseExceededDialog from "../../components/LicenseManagement/LicenseExceededDialog";

//Dialogs
import ArtworkManagement from "../../components/ArtworkManagement/ArtworkManagement";
import UserManagementAccessDialog from "../AccessManagement/UserAccessManagementDialog";

type TopBarProps = {
    handleProfileOpen: () => void;
    handleProfileClose: () => void;
};

export const TopBar: React.FC<TopBarProps> = ({ handleProfileOpen, handleProfileClose }) => {
    //License Checker
    const { canAdd } = useLicenseChecker();
    const [licenseCheck, setLicenseCheck] = useState<LicenseCheck | null>(null);
    const isTenantAdmin = true;
    const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

    const isAuthenticated = useIsAuthenticated();
    const { user, activeClientId, isAdmin, isOwner, isValuer, isSysAdmin, isFinance, activeClientLicense,
        compactView, showValues, dimensionDisplayPreference, dimensionUnitPreference, updateUserSettings  } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

    const { instance } = useMsal();
    const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
    const location = useLocation();
    const { currency, setCurrency } = useCurrency();
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const isOnHomePage = location.pathname === '/' || location.pathname === '/gallery' || location.pathname === '/dashboard';
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [eventsMenuAnchor, setEventsMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [artMenuAnchor, setArtMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [portfolioMenuAnchor, setPortfolioMenuAnchor] = React.useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const [refreshGallery, setRefreshGallery] = useState(false);
    const { filterParams, updateFilter } = useFilter();
    const [openClientAccess, setOpenClientAccess] = useState(false);

    const handleAddArtwork = async () => {
        const result = await canAdd("artwork");

        if (!result.allowed) {
            setLicenseCheck(result);
            setLicenseDialogOpen(true);
            return;
        }

        // Add New Arrtwork
        setOpenModal(true);
    };

    const handleUpgrade = () => {
        setLicenseDialogOpen(false);
        navigate("/admin/tenant");
    };

    //Selected Portfolio
    const clientName = user?.clients?.find(client => client.clientId === activeClientId)?.clientName || 'Unknown Client';

    //Gallery
    const handleGalleryNav = () => {
        navigate('/gallery');
    }

    //Dashboard
    const handleDashboardNav = () => {
        navigate('/dashboard');
    }

    //Add Artwork
    const [openModal, setOpenModal] = useState(false);
    const handleClose = () => setOpenModal(false);

    //Search
    const handleOpenSearch = () => setSearchOpen(true);
    const handleCloseSearch = () => setSearchOpen(false);
    
    const handleSaveComplete = () => {
        setOpenModal(false);
        setRefreshGallery(prev => !prev);
    };

    const [searchParams, setSearchParms] = useState<SearchData>({
        description: '',
        categoryIds: [],
        locations: [],
        minValue: null,
        maxValue: null,
        currency: '',
        valuationType: "acquisition"
    });

    const handleSearch = (searchData: SearchData) => {
        const updatedSearch: SearchData = {
            description: searchData.description,
            categoryIds: searchData.categoryIds,
            locations: searchData.locations,
            minValue: searchData.minValue,
            maxValue: searchData.maxValue,
            currency: searchData.currency,
            valuationType: searchData.valuationType
        }

        setSearchParms(updatedSearch);
        setSearchOpen(false);
    };

    const handleLogin = () => instance.loginRedirect(loginRequest);

    const handleSettingsOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setAnchorEl(null);
    };

    const handleEventsOpen = (event: React.MouseEvent<HTMLElement>) => {
        setEventsMenuAnchor(null);
        navigate('/events/view');
    };

    const handleEventsClose = () => {
        setEventsMenuAnchor(null);
    };

    const handlePortfolioOpen = (event: React.MouseEvent<HTMLElement>) => {
        setPortfolioMenuAnchor(null);
        navigate('/portfolios');
    };

    const handlePortfolioClose = () => {
        setPortfolioMenuAnchor(null);
    };

    const handleNavigate = (path: string) => {

        if (path === "profile") {

            handleProfileOpen();
        }
        else {
            navigate(path);
        }

        setDrawerOpen(false);
    };

    const handleCurrencyChange = (event: SelectChangeEvent) => {

        setCurrency(event.target.value as string);
    };

    const handleSearchClick = () => {
        setSearchOpen(true);
        handleOpenSearch();
    };

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    const handleArtMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        //onAddNewArtwork?.();
        setArtMenuAnchor(event.currentTarget);
    };

    const handleArtMenuClose = () => {
        setArtMenuAnchor(null);
    };
    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };


    // const handleCompactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     updateFilter({ compactView: event.target.checked });
    //     handleSettingsClose();
    // };

    // const handleShowValuesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     updateFilter({ showValues: event.target.checked });
    //     handleSettingsClose();
    // };

    const handleCompactChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        try {
            await updateUserSettings({
                compactView: event.target.checked
            });
        }
        catch (error) {
            console.error(
                "Failed to save compact view preference",
                error
            );
        }
        finally {
            handleSettingsClose();
        }
    };

    const handleShowValuesChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        try {
            await updateUserSettings({
                showValues: event.target.checked
            });
        }
        catch (error) {
            console.error(
                "Failed to save value preference",
                error
            );
        }
        finally {
            handleSettingsClose();
        }
    };

    const handleDimensionUnitChange = async (
        _event: React.MouseEvent<HTMLElement>,
        value: DimensionUnitType | null
    ) => {
        if (value === null)
            return;

        try {
            await updateUserSettings({
                dimensionUnitPreference: value
            });
        }
        catch (error) {
            console.error(
                "Failed to save dimension unit preference",
                error
            );
        }
        finally {
            handleSettingsClose();
        }
    };

    const handleDimensionDisplayChange = async (
        _event: React.MouseEvent<HTMLElement>,
        value: DimensionDisplayType | null
    ) => {
        if (value === null)
            return;

        try {
            await updateUserSettings({
                dimensionDisplayPreference: value
            });
        }
        catch (error) {
            console.error(
                "Failed to save dimension display preference",
                error
            );
        }
        finally {
            handleSettingsClose();
        }
    };

    const menuItems = [
        { text: 'Home', path: '/' },
        { text: 'Dashboard', path: '/dashboard' },
        { text: 'Portfolios', path: '/portfolios' },
        { text: 'Clients', path: '/settings/clients' },
        { text: 'Locations', path: '/settings/locations' },
        { text: 'Categories', path: '/settings/categories' },
        { text: 'Artists', path: '/settings/artists' },
        { text: 'Profile', path: 'profile' },
        { text: 'License', path: '/admin/tenant' },
    ];

    const fullSettingsMenu = (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleSettingsClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
        >

            <MenuItem onClick={() => { handleSettingsClose(); navigate('/settings/clients'); }} hidden={isViewOnly} >
                Manage Clients
            </MenuItem>
            <MenuItem onClick={() => { handleSettingsClose(); setOpenClientAccess(true); }} hidden={!isAdmin} >
                Manage Users
            </MenuItem>
            <MenuItem onClick={() => { handleSettingsClose(); navigate('/settings/locations'); }} hidden={isViewOnly} >
                Manage Locations
            </MenuItem>
            <MenuItem onClick={() => { handleSettingsClose(); navigate('/settings/categories'); }} hidden={isViewOnly} >
                Manage Categories
            </MenuItem>
            <MenuItem onClick={() => { handleSettingsClose(); navigate('/settings/artists'); }} hidden={isViewOnly} >
                Manage Artists
            </MenuItem>
            <MenuItem disableRipple className="menu-section-header"
                classes={{ root: 'menu-section-header' }}>
                <ListItemText primary="Display Options" />
            </MenuItem>

            <MenuItem disableRipple>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                //checked={!!filterParams.compactView}
                                checked={!!compactView}  // Use the compactView from user context
                                onChange={handleCompactChange}
                            />
                        }
                        label="Compact view"
                    />
                </FormGroup>
            </MenuItem>

            {isFinance &&
                <MenuItem disableRipple>
                    <FormGroup>
                        <FormControlLabel
                            control=
                            {
                                <Switch
                                    size="small"
                                    //checked={!!filterParams.showValues}
                                    checked={!!showValues} // Use the showValues from user context
                                    onChange={handleShowValuesChange}
                                />
                            }
                            label="Show values"
                        />
                    </FormGroup>
                </MenuItem>
            }

            <Divider />

            <MenuItem>
                <FormControl size="small">
                    <FormLabel
                        sx={{
                            fontSize: '0.875rem',
                            mb: 0.5
                        }}
                    >
                        Dimension units
                    </FormLabel>

                    <ToggleButtonGroup
                        value={dimensionUnitPreference}
                        exclusive
                        size="small"
                        onChange={handleDimensionUnitChange}
                        aria-label="dimension units"
                    >
                        <ToggleButton value={DimensionUnitType.Metric}>
                            Metric
                        </ToggleButton>

                        <ToggleButton value={DimensionUnitType.Imperial}>
                            Imperial
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </MenuItem>
            <MenuItem>
                <FormControl size="small">
                    <FormLabel
                        sx={{
                            fontSize: '0.875rem',
                            mb: 0.5
                        }}
                    >
                        Dimensions
                    </FormLabel>

                    <ToggleButtonGroup
                        value={dimensionDisplayPreference}
                        exclusive
                        size="small"
                        onChange={handleDimensionDisplayChange}
                        aria-label="dimension display"
                    >
                        <ToggleButton value={DimensionDisplayType.Unframed}>
                            Unframed
                        </ToggleButton>

                        <ToggleButton value={DimensionDisplayType.Framed}>
                            Framed
                        </ToggleButton>

                        <ToggleButton value={DimensionDisplayType.Both}>
                            Both
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </MenuItem>



            {isSysAdmin && 
                <>
                <MenuItem disableRipple className="menu-section-header"
                    classes={{ root: 'menu-section-header' }}>
                    <ListItemText primary="Admin Options" />
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/organisations'); }} hidden={isViewOnly} >
                    Organisations
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/sources'); }} hidden={isViewOnly} >
                Artwork Sources
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/statuses'); }} hidden={isViewOnly} >
                    Artwork Status
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/note-types'); }} hidden={isViewOnly} >
                    Note Types
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/sysAdmin'); }}>
                    System Admin
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/repair-diary') }}>
                    Repair Diary
                </MenuItem>
                <MenuItem onClick={() => { handleSettingsClose(); navigate('/admin/audit'); }}>
                    Audit Logs
                </MenuItem>
            </>
            }
        </Menu>
    );

    const partialSettingsMenu = (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleSettingsClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <MenuItem onClick={() => { handleSettingsClose(); navigate('/settings/clients'); }} hidden={isViewOnly} >
                Manage Clients
            </MenuItem>

            <MenuItem disableRipple className="menu-section-header" classes={{ root: 'menu-section-header' }}>
                <ListItemText primary="Display Options" />
            </MenuItem>

            <MenuItem disableRipple>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={!!compactView}
                                onChange={handleCompactChange}
                            />
                        }
                        label="Compact view"
                    />
                </FormGroup>
            </MenuItem>

            {isFinance &&
                <MenuItem disableRipple>
                    <FormGroup>
                        <FormControlLabel
                            control=
                            {
                                <Switch
                                    size="small"
                                    checked={!!showValues}
                                    onChange={handleShowValuesChange}
                                />
                            }
                            label="Show values"
                        />
                    </FormGroup>
                </MenuItem>
            }
            <Divider />

            <MenuItem>
                <FormControl size="small">
                    <FormLabel
                        sx={{
                            fontSize: '0.875rem',
                            mb: 0.5
                        }}
                    >
                        Dimension units
                    </FormLabel>

                    <ToggleButtonGroup
                        value={dimensionUnitPreference}
                        exclusive
                        size="small"
                        onChange={handleDimensionUnitChange}
                        aria-label="dimension units"
                    >
                        <ToggleButton value={DimensionUnitType.Metric}>
                            Metric
                        </ToggleButton>

                        <ToggleButton value={DimensionUnitType.Imperial}>
                            Imperial
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </MenuItem>
            <MenuItem>
                <FormControl size="small">
                    <FormLabel
                        sx={{
                            fontSize: '0.875rem',
                            mb: 0.5
                        }}
                    >
                        Dimensions
                    </FormLabel>

                    <ToggleButtonGroup
                        value={dimensionDisplayPreference}
                        exclusive
                        size="small"
                        onChange={handleDimensionDisplayChange}
                        aria-label="dimension display"
                    >
                        <ToggleButton value={DimensionDisplayType.Unframed}>
                            Unframed
                        </ToggleButton>

                        <ToggleButton value={DimensionDisplayType.Framed}>
                            Framed
                        </ToggleButton>

                        <ToggleButton value={DimensionDisplayType.Both}>
                            Both
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </MenuItem>
        </Menu>
    );

    const settingsMenu = canEditArtwork ? fullSettingsMenu : partialSettingsMenu;

    const userMenu = (
        <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={handleUserMenuClose}>
            <MenuItem disabled>{user?.displayName}</MenuItem>
            <MenuItem onClick={() => { handleProfileOpen(); handleUserMenuClose(); }}>
                Proflie Page
            </MenuItem>
            <MenuItem onClick={handleUserMenuClose}>
                <LogoutButton />
            </MenuItem>
        </Menu>
    );

    const artworkMenu = (
        <Menu anchorEl={artMenuAnchor} open={Boolean(artMenuAnchor)} onClose={handleArtMenuClose}>
            <MenuItem disabled={!isOnHomePage} onClick={() => { handleArtMenuClose(); handleAddArtwork(); }} hidden={isViewOnly}>
                Add Artwork
            </MenuItem>
            <MenuItem onClick={() => { handleArtMenuClose(); navigate('/movements'); }} hidden={isViewOnly}>
                Artwork Movements
            </MenuItem>
            {isFinance &&
                <MenuItem onClick={() => { handleArtMenuClose(); navigate('/payments'); }} hidden={isViewOnly}>
                    Artwork Payments
                </MenuItem>
            }
        </Menu>
    );

    const eventsMenu = (
        <Menu anchorEl={eventsMenuAnchor} open={Boolean(eventsMenuAnchor)} onClose={handleEventsClose}>
            <MenuItem onClick={() => { handleEventsClose(); navigate('/events/view'); }}>
                View Events & Exhibitions
            </MenuItem>
        </Menu>
    );

    return (
        <>
        <header className="topbar">
            {/* LEFT SECTION */}
            <div className="topbar-left">
                <Typography
                    variant="h6"
                    className="app-title"
                    onClick={() => navigate('/')}
                    sx={{
                        display: { xs: 'none', sm: 'block' } // Hide on mobile, show on small screens and up
                    }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="Logo"
                            sx={{
                                height: { xs: 24, sm: 28, md: 35 },
                                width: 'auto'
                            }}
                        />
                    Art Management Hub
                </Typography>

                    {isAuthenticated && user?.hasAcceptedTerms ? (
                        <>
                            {/* Hamburger Menu for Mobile */}
                            <IconButton
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer}
                                    sx={{ display: { xs: 'inline-block', sm: 'inline-block', lg: 'none' } }}
                            >
                                <MenuIcon />
                                </IconButton>

                            {/* Gallery */}
                            <Tooltip title="View Collections Gallery">
                                <button className="icon-btn hide-mobile" aria-label="Gallery" onClick={handleGalleryNav}>
                                    <CollectionsIcon />
                                    <span>Gallery</span>
                                </button>
                            </Tooltip>

                            {/* Dashboard */}
                            {(isFinance &&
                                <Tooltip title="View Dashboard">
                                    <button className="icon-btn hide-mobile" aria-label="Dashboard" onClick={handleDashboardNav}>
                                        <PieChart />
                                        <span>Dashboard</span>
                                    </button>
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <></>
                        )}
            </div>

            {/* CENTER SECTION */}
            <div className="topbar-center">
                <Typography
                    variant="caption"
                    className="app-title"
                    sx={{
                        display: { xs: 'block', sm: 'none', lg: 'none' }
                    }}
                >
                        {clientName}
                </Typography>
            </div>

            {/* RIGHT SECTION */}
            <div className="topbar-right">
            {isAuthenticated && user?.hasAcceptedTerms ? (
                    <>
                        <ClientSelector inMenuBar={true} />

                            {/* Currency Dropdown */}
                        <div className="currency-select-box">
                            <FormControl variant="outlined" className="currency-select">
                                    <InputLabel sx={{ color: 'white', backgroundColor:'#4a4444', display: { xs: 'none', sm: 'inline-block' } }}>Currency</InputLabel>
                            <Select
                                value={currency}
                                onChange={handleCurrencyChange}
                                label="Currency"
                                size="small"
                                className="currency-select"
                                sx={{ color: 'white', display: { xs: 'none', sm: 'inline-block' } }}
                            >
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="GBP">GBP</MenuItem>
                                <MenuItem value="JPY">JPY</MenuItem>
                            </Select>
                                </FormControl>
                        </div>

                        {/* Drawer Search */}
                        <DrawerSearch
                                defaultCurrency={currency}
                        />

                        {/* Artwork */}
                            {/* <Tooltip title="Add new artwork">
                                    <button className="icon-btn hide-mobile" aria-label="Add Artwork" onClick={handleAddArtwork}>
                                    <AddToPhotosIcon />
                                    <span>Add Artwork</span>
                                </button>
                                //</Tooltip>
                            */}
                            <Tooltip title="Artworks">
                                <button className="icon-btn hide-mobile" aria-label="Settings" onClick={handleArtMenuOpen}>
                                    <PaletteIcon />
                                    <span>Artworks</span>
                                </button>
                            </Tooltip>
                        {artworkMenu}



                        {/* Portfolio */}
                        <Tooltip title="View & Manage Portfolios">
                            <button className="icon-btn hide-mobile" aria-label="Portfolios" onClick={handlePortfolioOpen}>
                                <CasesIcon />
                                <span>Portfolios</span>
                            </button>
                        </Tooltip>

                        {/* Events */}
                        <Tooltip title="View Exhibitions & Events">
                            <button className="icon-btn hide-mobile" aria-label="Events" onClick={handleEventsOpen}>
                                <EventIcon />
                                <span>Calendar</span>
                            </button>
                        </Tooltip>
                        {eventsMenu}

                        {/* Settings */}
                        <Tooltip title="Settings">
                            <button className="icon-btn hide-mobile" aria-label="Settings" onClick={handleSettingsOpen}>
                                <SettingsIcon />
                                <span>Settings</span>
                            </button>
                            </Tooltip>
                                    {settingsMenu}

                    <Tooltip title="Logout">
                            <button className="primary-btn hide-mobile" aria-label="Profile" onClick={handleUserMenuOpen}>
                                <AccountCircle />
                                <span>Profile</span>
                            </button>
                    </Tooltip>
                    {userMenu}
                </>
            ) : (
                    <></>
            )}

            </div>

            </header>

            <ArtworkManagement isOpen={openModal} onClose={handleClose} onSaveComplete={handleSaveComplete} artworkId={null} />

            <UserManagementAccessDialog
                open={openClientAccess}
                onClose={() => setOpenClientAccess(false)}
                scope={{ kind: "client", clientId: activeClientId! }}
            />

            {/* Drawer for Mobile Menu */}
            <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer}>
                <ClientSelector inMenuBar={false} />
                <List sx={{ width: 250 }}>
                    {menuItems.map((item, index) => (
                        <ListItem
                            key={index}
                            component="button"
                            onClick={() => {
                                handleNavigate(item.path);
                            }}
                        >
                            <ListItemText primary={item.text} />
                        </ListItem>
                    ))}
                    <Divider />

                </List>
            </Drawer>

            {/* License Position Dialog */}
            <LicenseExceededDialog
                open={licenseDialogOpen}
                onClose={() => setLicenseDialogOpen(false)}
                licenseCheck={licenseCheck}
                onUpgrade={handleUpgrade}
                isTenantAdmin={isTenantAdmin}
            />

        </>
    );
};
